import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Platform } from 'react-native';
import { storage } from '../utils/storage';
import { platformBackgrounds } from '../styles/platformBackgrounds';
import { ACCENT_TEXT } from '../styles/accentText';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeColors {
  // Surfaces
  canvas: string;
  /** CORE-639 — page background token. WEB: transparent, so the global
   *  background layer (body::before, §2) shows through page/section
   *  containers. NATIVE: identical to `canvas` — native keeps its opaque
   *  page fill untouched. Use this for page/section roots; keep `canvas`
   *  for solid surfaces (menus, placeholders) that must stay opaque. */
  pageBg: string;
  /** CORE-1733 — непрозрачная основа общего фона страницы (`--bg-base` в
   *  public/global.css, статья 110756133). Для мест, где картинка растворяется
   *  в фон краской, а не маской: такой переход должен уходить в цвет страницы,
   *  иначе на стыке остаётся полоса. NATIVE: равен `canvas`. */
  pageBase: string;
  surface: string;
  surfaceElevated: string;
  /** CORE-1133 — подложка модального окна, НЕПРОЗРАЧНАЯ в обеих темах.
   *
   *  Остальные surface-токены на вебе намеренно полупрозрачны (CORE-639 §1):
   *  сквозь карточки и панели просвечивает общий фон страницы, и это так
   *  задумано. Модальному окну это противопоказано — оно обязано отделять
   *  редактируемое от того, что под ним. QA 29.09 поймала ровно это: сквозь
   *  форму редактирования записи читались название фоновой карточки, фильтры,
   *  счётчики и граница футера.
   *
   *  Поэтому отдельный токен, а не «взять тот, что поплотнее»: у системы не
   *  было имени для понятия «здесь просвечивать нельзя», и каждый автор
   *  модалки выбирал на глаз. */
  surfaceDialog: string;
  /** Subtle warm block — chat bubble (other), agapē banner backing. */
  surfaceWarm: string;
  /** Soft tint — secondary blocks, agapē practice banner. Same value as
   *  `backgroundSoft` — both keys ship so existing call sites don't churn. */
  surfaceSoft: string;
  backgroundSoft: string;
  backgroundAlt: string;

  // CORE-639 §3 — semantic surface tokens. WEB: semi-transparent so the
  // global background layer shows through cards/panels (§1 values). NATIVE:
  // opaque fallbacks (native has no global bg layer — untouched).
  surfacePrimary: string;    // large cards / panels
  surfaceSecondary: string;  // nested / secondary surfaces
  surfaceHover: string;      // hover surface
  surfaceActive: string;     // active / selected surface
  headerBg: string;          // header backing
  focusRing: string;         // outer focus ring
  shadowSurface: string;     // soft panel shadow (web boxShadow string)

  // Text
  textPrimary: string;       // fg-1 — primary
  textSecondary: string;     // fg-2 — body
  textMuted: string;         // fg-3 — captions

  // Accent
  /** CORE-1733 — оранжевый как цвет текста или значимого глифа (не заливка):
   *  #9A421F светлая / #F0A06D тёмная на вебе через CSS-переменную, см.
   *  styles/accentText.ts. `solar` остаётся для заливок и бренд-элементов. */
  accentText: string;
  solar: string;
  fire: string;
  ctaStart: string;
  ctaEnd: string;
  ctaHoverStart: string;
  ctaHoverEnd: string;
  ctaPressedStart: string;
  ctaPressedEnd: string;
  ctaOutline: string;
  ctaShadow: string;

  // Charcoal role (used as inverse-surface for primary CTAs; inverts on dark)
  charcoal: string;

  // Borders
  border: string;
  borderSubtle: string;
  /** Stronger hairline — outlined buttons, dividers. */
  borderStrong: string;

  // Semantic
  error: string;
  success: string;
  warning: string;
  info: string;
  online: string;
  star: string;
  link: string;
}

interface ThemeContextType {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  isDark: boolean;
  colors: ThemeColors;
  setMode: (mode: ThemeMode) => void;
  cycleMode: () => void;
}

// Native light theme runs 2–3 shades darker than web (owner request) for higher
// on-device contrast; every darkened token is forked so the deployed web look is
// unchanged. Web branch = the original light palette.
const WEB = Platform.OS === 'web';
const L_CANVAS = WEB ? '#FFFFFF' : '#fdf6f3';   // page background
const L_SURFACE = WEB ? '#FFFFFF' : '#f7efe8';  // cards / sidebars — a shade darker
const L_ELEVATED = WEB ? '#FFFFFF' : '#f4ebe4'; // modals / cards — a touch more

const lightColors: ThemeColors = {
  canvas: L_CANVAS,
  pageBg: WEB ? 'transparent' : L_CANVAS,   // web: reveal global bg layer
  pageBase: WEB ? platformBackgrounds.light.pageBase : L_CANVAS, // CORE-1733 — = --bg-base (светлая v4)
  // §1 web: surface = --surface-primary, elevated (modals) = --surface-hover.
  surface: WEB ? 'rgba(255, 255, 255, 0.78)' : L_SURFACE,
  surfaceElevated: WEB ? 'rgba(255, 255, 255, 0.90)' : L_ELEVATED,
  surfaceDialog: L_ELEVATED,      // CORE-1133 — без альфы: модалка не просвечивает
  surfaceWarm: '#FAFAF8',         // chat bubble (other), subtle warm blocks
  // §1 --surface-secondary (web): nested/secondary blocks show the bg through.
  surfaceSoft: WEB ? 'rgba(255, 255, 255, 0.58)' : '#F4F7F6',
  backgroundSoft: WEB ? 'rgba(255, 255, 255, 0.58)' : '#F4F7F6',
  backgroundAlt: '#F1DAC3',       // Official Warm Beige
  // §3 surfaces — web semi-transparent (§1 light), native opaque fallback.
  surfacePrimary: WEB ? 'rgba(255, 255, 255, 0.78)' : L_SURFACE,
  surfaceSecondary: WEB ? 'rgba(255, 255, 255, 0.58)' : L_ELEVATED,
  surfaceHover: WEB ? 'rgba(255, 255, 255, 0.90)' : L_SURFACE,
  surfaceActive: WEB ? 'rgba(255, 249, 242, 0.96)' : L_SURFACE,
  // CORE-1733 — подложка шапки v4 (её градиент рисует Header, статья 110756133).
  headerBg: WEB ? platformBackgrounds.light.headerBase : L_CANVAS,
  focusRing: 'rgba(233, 130, 69, 0.36)',
  shadowSurface: '0 14px 40px rgba(111, 78, 55, 0.075), 0 0 26px rgba(205, 157, 122, 0.045)',
  // CORE-639 §1 — web text/borders on the new palette; native keeps originals.
  textPrimary: WEB ? '#2B2730' : '#000A10',     // §1 --text-primary (web)
  textSecondary: WEB ? '#625B67' : '#332F2A',   // §1 --text-secondary (web)
  // CORE-1733 — на сливочной основе v4 прежний #847D88 давал 3.7:1 (у левого
  // края страницы 3.1:1) — ниже 4.5 для обычного текста, а приглушённым здесь
  // набраны и абзацы, и плейсхолдеры. #686170 держит ≥4.5 на самом тёмном
  // открытом месте светлой страницы и остаётся светлее вторичного #625B67.
  textMuted: WEB ? '#686170' : '#57534C',
  accentText: ACCENT_TEXT, // CORE-1733
  solar: '#E98245',
  fire: '#E98245',
  ctaStart: '#F45122',
  ctaEnd: '#FF955D',
  ctaHoverStart: '#E94A1D',
  ctaHoverEnd: '#FF864B',
  ctaPressedStart: '#D94318',
  ctaPressedEnd: '#F4773E',
  ctaOutline: '#F26A36',
  ctaShadow: '0 14px 34px rgba(184, 83, 42, 0.22)',
  charcoal: WEB ? '#00121D' : '#000A10',         // = primary; inverts in dark
  border: WEB ? 'rgba(214, 153, 113, 0.24)' : 'rgba(0, 18, 29, 0.16)',
  borderSubtle: WEB ? 'rgba(214, 153, 113, 0.17)' : 'rgba(0, 18, 29, 0.08)',
  borderStrong: WEB ? 'rgba(202, 132, 88, 0.36)' : 'rgba(0, 18, 29, 0.28)',
  error: '#E53E3E',
  success: '#38A169',
  warning: '#D69E2E',
  info: '#3182CE',
  online: '#22C55E',
  star: '#FBBF24',
  link: '#3B82F6',
};

const darkColors: ThemeColors = {
  // Calm neutral graphite palette — owner asked away from blue-tinted dark
  // (#0F1A22 / #162130 / #1E2D3D read as cold light-blue) and away from the
  // earlier brown #1E1B1A. These RGB values have B-R delta ≤ 6, almost true
  // gray, with a hair of cool to keep depth in non-OLED panels.
  canvas: '#202226',              // CORE-974 — warm graphite page background
  pageBg: WEB ? 'transparent' : '#202226',  // web: reveal global bg layer
  pageBase: platformBackgrounds.dark.pageBase, // CORE-1733 — = --bg-base (тёмная)
  // §1 web dark: surface = --surface-primary, elevated = --surface-hover.
  surface: WEB ? 'rgba(40, 39, 37, 0.84)' : '#282725',           // Cards / sidebars
  surfaceElevated: WEB ? 'rgba(48, 46, 43, 0.90)' : '#302E2B',   // Modals / drawers
  surfaceDialog: '#302E2B',        // CORE-1133 — без альфы: модалка не просвечивает
  surfaceWarm: '#282725',          // Chat bubble (other), subtle blocks
  // §1 --surface-secondary (web dark): nested/secondary blocks show bg through.
  surfaceSoft: WEB ? 'rgba(44, 43, 40, 0.68)' : '#282725',
  backgroundSoft: WEB ? 'rgba(44, 43, 40, 0.68)' : '#282725',
  backgroundAlt: '#282725',        // Subtle warm alt background
  // §3 surfaces — web semi-transparent (§1 dark), native opaque fallback.
  surfacePrimary: WEB ? 'rgba(40, 39, 37, 0.84)' : '#282725',
  surfaceSecondary: WEB ? 'rgba(44, 43, 40, 0.68)' : '#302E2B',
  surfaceHover: WEB ? 'rgba(50, 48, 44, 0.90)' : '#302E2B',
  surfaceActive: WEB ? 'rgba(58, 53, 47, 0.94)' : '#342F2A',
  headerBg: WEB ? platformBackgrounds.dark.headerBase : '#202226', // CORE-1733 — подложка шапки v4
  focusRing: 'rgba(244, 196, 83, 0.42)',
  shadowSurface: '0 16px 46px rgba(0, 0, 0, 0.22), 0 0 28px rgba(126, 91, 68, 0.05)',
  // CORE-639 §1 — web text/borders on the new dark palette; native keeps originals.
  textPrimary: WEB ? '#F3ECE3' : '#F5F0EB',       // §1 --text-primary (web)
  textSecondary: WEB ? '#C7BEC5' : '#B0A89E',     // §1 --text-secondary (web)
  // CORE-1733 (возврат QA 23.10) — #9C949D на поверхностях v4 давал 4.38–4.49:1,
  // в тёплом углу поверхности 4.0. #A8A0A9 держит ≥4.67 на всех слоях тёмной
  // темы (страница, поверхность, выбранная поверхность) и темнее вторичного.
  textMuted: WEB ? '#A8A0A9' : '#b09e97',         // §1 --text-muted (web)
  accentText: ACCENT_TEXT, // CORE-1733
  solar: '#E98245',               // Solar identical in both themes
  fire: '#E98245',
  ctaStart: '#F45122',
  ctaEnd: '#FF955D',
  ctaHoverStart: '#FF642F',
  ctaHoverEnd: '#FFA06D',
  ctaPressedStart: '#D94318',
  ctaPressedEnd: '#F4773E',
  ctaOutline: '#FF814A',
  ctaShadow: '0 16px 38px rgba(10, 9, 8, 0.34)',
  charcoal: '#F5F0EB',            // Inverted — high-contrast inverse surface
  border: WEB ? 'rgba(241, 181, 142, 0.20)' : 'rgba(255, 255, 255, 0.08)',
  borderSubtle: WEB ? 'rgba(241, 181, 142, 0.13)' : 'rgba(255, 255, 255, 0.04)',
  borderStrong: WEB ? 'rgba(241, 181, 142, 0.30)' : 'rgba(255, 255, 255, 0.15)',
  error: '#FC8181',
  success: '#68D391',
  warning: '#F6E05E',
  info: '#63B3ED',
  online: '#22C55E',
  star: '#FBBF24',
  link: '#63B3ED',
};

function getSystemTheme(): ResolvedTheme {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') return getSystemTheme();
  return mode;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: 'system',
  resolved: 'light',
  isDark: false,
  colors: lightColors,
  setMode: () => { },
  cycleMode: () => { },
});

// Read saved theme synchronously on web to avoid a light-flash before the
// async storage promise resolves. localStorage is always available in a browser
// context; the Platform guard keeps this from running on native.
function readSavedMode(): ThemeMode {
  if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem('agape_theme_mode');
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  }
  return 'light';
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(() => readSavedMode());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(readSavedMode()));

  const applyTheme = useCallback((r: ResolvedTheme) => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const root = document.documentElement;
      root.setAttribute('data-theme', r);
      // Single source of truth: mirror the resolved JS palette onto CSS
      // custom properties so web stylesheets (global.css, Stream overrides)
      // read the exact same values as RN components — no drift, no second
      // hardcoded palette to keep in sync.
      const p = r === 'dark' ? darkColors : lightColors;
      const vars: Record<string, string> = {
        '--ag-canvas': p.canvas,
        '--ag-surface': p.surface,
        '--ag-surface-elevated': p.surfaceElevated,
        '--ag-surface-warm': p.surfaceWarm,
        '--ag-surface-soft': p.surfaceSoft,
        '--ag-bg-alt': p.backgroundAlt,
        '--ag-bg-soft': p.backgroundSoft,
        '--ag-text-primary': p.textPrimary,
        '--ag-text-secondary': p.textSecondary,
        '--ag-text-muted': p.textMuted,
        '--ag-solar': p.solar,
        '--ag-fire': p.fire,
        '--ag-cta-start': p.ctaStart,
        '--ag-cta-end': p.ctaEnd,
        '--ag-cta-hover-start': p.ctaHoverStart,
        '--ag-cta-hover-end': p.ctaHoverEnd,
        '--ag-cta-pressed-start': p.ctaPressedStart,
        '--ag-cta-pressed-end': p.ctaPressedEnd,
        '--ag-cta-outline': p.ctaOutline,
        '--ag-cta-shadow': p.ctaShadow,
        '--ag-charcoal': p.charcoal,
        '--ag-border': p.border,
        '--ag-border-subtle': p.borderSubtle,
        '--ag-border-strong': p.borderStrong,
        '--ag-error': p.error,
        '--ag-success': p.success,
        '--ag-warning': p.warning,
        '--ag-info': p.info,
        '--ag-online': p.online,
        '--ag-star': p.star,
        '--ag-link': p.link,
      };
      for (const k in vars) root.style.setProperty(k, vars[k]);
    }
  }, []);

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    storage.setThemeMode(newMode);
    const r = resolveTheme(newMode);
    setResolved(r);
    applyTheme(r);
  }, [applyTheme]);

  const cycleMode = useCallback(() => {
    const next: ThemeMode = mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light';
    setMode(next);
  }, [mode, setMode]);

  // Apply CSS variables on first paint (synchronous init already set JS state,
  // but CSS custom properties on document need a DOM call — that can only
  // happen after mount, so this effect runs once immediately after mount).
  useEffect(() => {
    applyTheme(resolved);
    // Also sync with AsyncStorage for native (no-op on web since we already
    // read localStorage; this keeps the two stores in agreement).
    storage.getThemeMode().then((saved) => {
      // CORE-1733 (возврат QA 28.10) — то же правило, что у readSavedMode: мусорное
      // значение (`auto`) раньше уходило прямо в data-theme.
      if (saved !== 'light' && saved !== 'dark' && saved !== 'system') return;
      const m: ThemeMode = saved;
      if (m === mode) return; // already correct from sync read — skip re-render
      setModeState(m);
      const r = resolveTheme(m);
      setResolved(r);
      applyTheme(r);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for system preference changes
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (mode === 'system') {
        const r = getSystemTheme();
        setResolved(r);
        applyTheme(r);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode, applyTheme]);

  const isDark = resolved === 'dark';
  const colors = isDark ? darkColors : lightColors;

  const value = useMemo(() => ({
    mode,
    resolved,
    isDark,
    colors,
    setMode,
    cycleMode,
  }), [mode, resolved, isDark, colors, setMode, cycleMode]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
